import { Button, Segmented, Select } from 'antd'
import { useId, useState } from 'react'
import { MdExpandLess, MdExpandMore } from 'react-icons/md'

import { cx } from '@/Ship/Helpers/cssClass'

import type { ReportFilterState, ReportHired, ReportListFilter, ReportSource } from '../Contracts/Report'
import { countExtraFilters, EXTRA_LIST_FILTERS } from '../Helpers/reportQuery'
import { REPORT_OPTION_SOURCES } from '../Helpers/reportSources'
import { ReportEntitySelect } from './ReportEntitySelect'
import { ReportPeriodField, Field } from './ReportFilterFields'

import styles from './Report.module.css'

const MAIN_LIST_FILTERS: ReportListFilter[] = ['carrier', 'driver', 'vehicle']

const SOURCE_OPTIONS: { value: ReportSource; label: string }[] = [
    { value: 'all', label: 'Усі' },
    { value: 'requests', label: 'З заявками' },
    { value: 'standalone', label: 'Без заявок' },
]

const HIRED_OPTIONS: { value: ReportHired; label: string }[] = [
    { value: 'all', label: 'Усі' },
    { value: 'true', label: 'Так' },
    { value: 'false', label: 'Ні' },
]

interface ReportFiltersProps {
    value: ReportFilterState
    onChange: (value: ReportFilterState) => void
}

/**
 * Смуга фільтра звіту (як у списках — застосовується одразу, без кнопок): період
 * підтвердження зі швидкими періодами, джерело задач, основні довідники (перевізник, водій,
 * ТЗ); решта — за «Ще фільтри». Скидання — чіпами в ReportShell.
 */
export const ReportFilters = ({ value, onChange }: ReportFiltersProps) => {
    const extraCount = countExtraFilters(value)
    // Блок розгорнутий, якщо в ньому щось задано — щоб активний фільтр не був прихований
    const [expanded, setExpanded] = useState(extraCount > 0)
    const baseId = useId()

    const setList = (name: ReportListFilter, options: ReportFilterState['lists'][ReportListFilter]) =>
        onChange({ ...value, lists: { ...value.lists, [name]: options } })

    const listField = (name: ReportListFilter) => {
        const id = `${baseId}-${name}`
        return (
            <Field key={name} label={REPORT_OPTION_SOURCES[name].label} htmlFor={id}>
                <ReportEntitySelect
                    id={id}
                    source={REPORT_OPTION_SOURCES[name]}
                    value={value.lists[name]}
                    onChange={(options) => setList(name, options)}
                />
            </Field>
        )
    }

    return (
        <div className={cx(styles, 'filters')}>
            <div className={cx(styles, 'filterRow')}>
                <ReportPeriodField
                    label="Період (дата підтвердження диспетчером)"
                    value={value.period}
                    onChange={(period) => onChange({ ...value, period })}
                    allowClear
                />
                <Field label="Задачі">
                    <Segmented<ReportSource>
                        options={SOURCE_OPTIONS}
                        value={value.source}
                        onChange={(source) => onChange({ ...value, source })}
                    />
                </Field>
            </div>

            <div className={cx(styles, 'filterRow')}>
                {MAIN_LIST_FILTERS.map(listField)}
                {expanded && (
                    <>
                        {EXTRA_LIST_FILTERS.map(listField)}
                        <Field label="Найманий транспорт" htmlFor={`${baseId}-hired`}>
                            <Select<ReportHired>
                                id={`${baseId}-hired`}
                                options={HIRED_OPTIONS}
                                value={value.isHired}
                                onChange={(isHired) => onChange({ ...value, isHired })}
                            />
                        </Field>
                    </>
                )}
                <div className={cx(styles, 'moreWrap')}>
                    <Button
                        type="link"
                        size="small"
                        className={cx(styles, 'more')}
                        icon={expanded ? <MdExpandLess /> : <MdExpandMore />}
                        aria-expanded={expanded}
                        onClick={() => setExpanded((open) => !open)}
                    >
                        {expanded ? 'Менше фільтрів' : extraCount > 0 ? `Ще фільтри (${extraCount})` : 'Ще фільтри'}
                    </Button>
                </div>
            </div>
        </div>
    )
}
